import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateUserDto } from '@/types/dtos/user/request/update-user.dto';
import type { UserDto } from '@/types/dtos/user/response/user.dto';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { useUserStore } from '@/state/stores/use-user-store';
import { GET_USER_QUERY_KEY } from '@/state/queries/users/use-get-user';

export function useUpdateUserMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);

  return useMutation({
    mutationFn: async (data: UpdateUserDto) => {
      const token = await getToken();
      return updateUser(data, token ?? '');
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER_QUERY_KEY] });
      const previousUser = queryClient.getQueryData<UserDto>([GET_USER_QUERY_KEY]);

      if (data.activeOrganization && previousUser) {
        queryClient.setQueryData<UserDto>([GET_USER_QUERY_KEY], {
          ...previousUser,
          activeOrganization: data.activeOrganization,
        });
      }

      return { previousUser };
    },
    onError: (_error, _data, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER_QUERY_KEY], context.previousUser);
      }
    },
    onSuccess: (updated: UserDto) => {
      queryClient.setQueryData([GET_USER_QUERY_KEY], (prev?: UserDto) => ({
        ...(prev ?? {}),
        ...updated,
      }));

      setUser({
        firstName: updated.firstName,
        lastName: updated.lastName,
        phoneNumber: updated.phoneNumber,
        language: updated.language,
        isOnboarded: updated.isOnboarded,
        usageProfile: updated.usageProfile,
        functional: updated.functional,
        analytical: updated.analytical,
        marketing: updated.marketing,
        activeOrganization: updated.activeOrganization,
      });
    },
  });
}

const updateUser = async (data: UpdateUserDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/users`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: data, token });
};
